/**
 * Utility for sanitizing user input that should be treated as plain text,
 * not HTML. Uses a proper state-machine approach to strip HTML tags,
 * avoiding the known bypasses of regex-based strippers.
 */
export class SanitizeUtil {
  /**
   * Removes all HTML tags and event handlers from input, safely.
   * Preserves whitespace during typing; trimming belongs to individual fields.
   * Uses a state machine to properly tokenize HTML and discard tags,
   * handling nested and malformed markup correctly.
   */
  static stripHtml(input: string | null | undefined): string {
    if (input == null) return '';
    const text = String(input);

    // Fast path for strings without angle brackets
    if (!text.includes('<') && !text.includes('>')) {
      return text;
    }

    let result = '';
    let inTag = false;
    let inClosingTag = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (char === '<') {
        inTag = true;
        inClosingTag = text[i + 1] === '/';
        continue;
      }

      if (char === '>') {
        inTag = false;
        inClosingTag = false;
        continue;
      }

      if (!inTag) {
        result += char;
      }
    }

    // Decode and strip remaining HTML entities
    result = SanitizeUtil.decodeHtmlEntities(result);
    return result;
  }

  /**
   * Decodes common HTML entities to prevent entity-based injection
   * and strips any remaining angle brackets.
   */
  private static decodeHtmlEntities(text: string): string {
    const entityMap: Record<string, string> = {
      '&lt;': '<',
      '&gt;': '>',
      '&amp;': '&',
      '&quot;': '"',
      '&#39;': "'",
      '&apos;': "'",
      '&#x27;': "'",
      '&#x2F;': '/',
      '&#x60;': '`',
    };

    let decoded = text;
    let iterations = 0;
    let prev = '';

    // Decode up to 3 times to handle double/triple encoding
    while (iterations < 3 && decoded !== prev) {
      prev = decoded;
      decoded = decoded.replace(/&(#x?[0-9a-fA-F]+|lt|gt|amp|quot|apos|#39|#x27|#x2F|#x60);/gi, (match) => {
        const lower = match.toLowerCase();
        return entityMap[lower] ?? entityMap[match] ?? match;
      });
      iterations++;
    }

    // Final sweep: strip any raw angle brackets/quotes that remain
    return decoded.replace(/[<>"'`]/g, '');
  }

  /**
   * Sanitizes generic text input against script injection while preserving safe characters.
   * Strips script/style/iframe constructs, dangerous protocol handlers, inline
   * event handlers, and converts raw HTML control characters to safe text.
   */
  static sanitizeInput(input: string | null | undefined): string {
    if (!input) return '';

    let sanitized = String(input);

    // 1. Remove script / style / iframe tags and contents
    sanitized = sanitized.replace(/<(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\/\1>/gi, '');

    // 2. Remove all HTML tags
    sanitized = sanitized.replace(/<[^>]*>/g, '');

    // 3. Strip dangerous protocol handlers (javascript:, data:, vbscript:)
    sanitized = sanitized.replace(/javascript\s*:/gi, '');
    sanitized = sanitized.replace(/data\s*:/gi, '');
    sanitized = sanitized.replace(/vbscript\s*:/gi, '');

    // 4. Remove inline event handlers (e.g. onerror=, onload=)
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');

    // 5. Convert raw HTML control characters (<, >, ") to safe text or clean them
    sanitized = sanitized
      .replace(/</g, '')
      .replace(/>/g, '')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');

    return sanitized;
  }

  /** Validates whether an email string matches a basic valid email pattern. */
  static isValidEmail(email: string): boolean {
    if (!email) return false;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }

  /** Validates institutional usernames (alphanumeric, dots, underscores, dashes, @). */
  static isValidUsername(username: string): boolean {
    if (!username) return false;
    const usernameRegex = /^[a-zA-Z0-9._%+-@]{3,100}$/;
    return usernameRegex.test(username.trim());
  }
}
